/**
 * Deterministic mock fundamentals + option-stats per symbol.
 *
 * In production, these come from:
 *  - NGX/X-Issuer Portal data feed (fundamentals, dividends, earnings)
 *  - Bloomberg/FactSet equivalents for analyst notes
 *  - Internal volatility surface for IV/HV/Implied-Move
 *
 * Here every value is seeded from `(symbol, fieldName)` so the UI looks alive
 * across refreshes without any backend, and so deterministic tests are easy.
 */
export interface OptionStats {
  impliedVolatility: number;     // %
  historicalVolatility: number;  // %
  ivPercentile: number;          // %
  putCallRatio: number;
  totalOpenInterest: number;
  impliedMovePct: number;        // %
}

export interface QuoteDetails {
  bid: number; bidSize: number;
  ask: number; askSize: number;
  open: number; high: number; low: number; close: number;
  volume: number; vwap: number;
  marketCap: number;             // ₦
  prevClose: number;
  range52W: { lo: number; hi: number };
}

export interface Fundamentals {
  pe: number; eps: number; bookValue: number;
  divYieldPct: number; marketCap: number;
  beta: number; sharesOutMillions: number;
  sector: string; industry: string;
}

export interface AnalystReport {
  firm: string; date: string; rating: 'Buy' | 'Hold' | 'Sell' | 'Overweight' | 'Underweight';
  targetPrice: number; note: string;
}

export interface CalendarEvent {
  kind: 'Earnings' | 'Dividend' | 'AGM';
  date: string; details: string;
}

function seed(s: string, salt: string): number {
  let h = 5381;
  const all = s + '|' + salt;
  for (let i = 0; i < all.length; i++) h = ((h << 5) + h) + all.charCodeAt(i);
  return Math.abs(h);
}
function rand(s: string, salt: string): number { return (seed(s, salt) % 100000) / 100000; }

export function optionStatsFor(symbol: string, mark: number): OptionStats {
  return {
    impliedVolatility:    20 + rand(symbol, 'iv') * 30,
    historicalVolatility: 15 + rand(symbol, 'hv') * 25,
    ivPercentile:         Math.round(rand(symbol, 'ivp') * 100),
    putCallRatio:         +(0.2 + rand(symbol, 'pcr') * 1.6).toFixed(2),
    totalOpenInterest:    Math.round(50_000 + rand(symbol, 'oi') * 500_000),
    impliedMovePct:       +(2 + rand(symbol, 'im') * 6).toFixed(1),
  };
  void mark;
}

export function quoteDetailsFor(symbol: string, mark: number): QuoteDetails {
  const tick = Math.max(0.01, mark * 0.001);
  const bid = +(mark - tick).toFixed(2); const ask = +(mark + tick).toFixed(2);
  const dayRange = mark * 0.03;
  return {
    bid, bidSize: 100 + (seed(symbol, 'bs') % 900),
    ask, askSize: 100 + (seed(symbol, 'as') % 900),
    open: +(mark - dayRange / 2).toFixed(2),
    high: +(mark + dayRange).toFixed(2),
    low:  +(mark - dayRange).toFixed(2),
    close: mark, prevClose: +(mark + 0.39).toFixed(2),
    volume: 200_000 + (seed(symbol, 'vol') % 2_000_000),
    vwap: +(mark - tick / 2).toFixed(2),
    marketCap: Math.round(mark * (5_000_000_000 + (seed(symbol, 'cap') % 50_000_000_000))),
    range52W: { lo: +(mark * 0.6).toFixed(2), hi: +(mark * 1.4).toFixed(2) },
  };
}

export function fundamentalsFor(symbol: string, mark: number): Fundamentals {
  return {
    pe: +(5 + rand(symbol, 'pe') * 25).toFixed(2),
    eps: +(mark / (5 + rand(symbol, 'pe') * 25)).toFixed(2),
    bookValue: +(mark * (0.5 + rand(symbol, 'bv') * 0.8)).toFixed(2),
    divYieldPct: +(rand(symbol, 'dy') * 8).toFixed(2),
    marketCap: Math.round(mark * (5_000_000_000 + (seed(symbol, 'cap') % 50_000_000_000))),
    beta: +(0.4 + rand(symbol, 'beta') * 1.4).toFixed(2),
    sharesOutMillions: Math.round(1000 + rand(symbol, 'so') * 30000),
    sector: SECTOR[symbol] ?? 'Industrials',
    industry: INDUSTRY[symbol] ?? 'Diversified',
  };
}

export function analystReportsFor(symbol: string): AnalystReport[] {
  const firms = ['Cordros Capital', 'CardinalStone', 'CSL Stockbrokers', 'Meristem', 'Vetiva', 'United Capital'];
  const ratings: AnalystReport['rating'][] = ['Buy', 'Hold', 'Sell', 'Overweight', 'Underweight'];
  return firms.slice(0, 4 + (seed(symbol, 'firms') % 3)).map((firm, i) => {
    const r = ratings[seed(symbol, 'r' + i) % ratings.length]!;
    const tgt = +(50 + rand(symbol, 'tgt' + i) * 1500).toFixed(2);
    return {
      firm, date: new Date(Date.now() - i * 7 * 24 * 3600 * 1000).toISOString().slice(0, 10),
      rating: r, targetPrice: tgt,
      note: `${firm} reiterates ${r} with a 12-month target of ₦${tgt.toFixed(2)}.`,
    };
  });
}

export function calendarFor(symbol: string): CalendarEvent[] {
  const now = Date.now(); const day = 24 * 3600 * 1000;
  return [
    { kind: 'Earnings', date: new Date(now + 14 * day).toISOString().slice(0, 10), details: 'Q3 results expected pre-market.' },
    { kind: 'Dividend', date: new Date(now + 28 * day).toISOString().slice(0, 10), details: `Interim dividend ex-date · symbol ${symbol}.` },
    { kind: 'AGM',      date: new Date(now + 60 * day).toISOString().slice(0, 10), details: '2026 Annual General Meeting.' },
  ];
}

const SECTOR: Record<string, string> = {
  ZENITHBANK: 'Financials', GTCO: 'Financials', FIRSTHOLDCO: 'Financials', UBA: 'Financials', STANBIC: 'Financials', ACCESSCORP: 'Financials',
  DANGCEM: 'Industrials', BUACEMENT: 'Industrials', WAPCO: 'Industrials', BETAGLAS: 'Industrials',
  MTNN: 'ICT', AIRTELAFRI: 'ICT', CWG: 'ICT',
  SEPLAT: 'Energy', GEREGU: 'Utilities', TRANSPOWER: 'Utilities', ARADEL: 'Energy', OANDO: 'Energy',
  NESTLE: 'Consumer Goods', BUAFOODS: 'Consumer Goods', PRESCO: 'Consumer Goods', DANGSUGAR: 'Consumer Goods', VITAFOAM: 'Consumer Goods', NB: 'Consumer Goods', NNFM: 'Consumer Goods',
  SFSREIT: 'Real Estate',
};
const INDUSTRY: Record<string, string> = {
  ZENITHBANK: 'Tier-1 Bank', GTCO: 'Tier-1 Bank', DANGCEM: 'Cement', MTNN: 'Mobile Telecoms', SEPLAT: 'Upstream Oil & Gas',
};
